import React, { useEffect, useState, useCallback, useRef } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import './AdminTeachers.scss';
import TabsContentHeader from '../../components/AdminDashboardTabsContentHeader/TabsContentHeader';
import images from '../../assets/images';
import { useLazyQuery } from '@apollo/client';
import { GET_TEACHERS_LIST, GET_TEACHER_BY_ID } from '../AdminDashboard/api/AdminDashboardQuery';
import { capitalizeFirstLetterOfEachWord, getScreenDimensions } from '../../utils/helper';
import AdminTeacherForm from './components/AdminTeacherForm';
import { debounce } from 'lodash';
import Loader from '../../components/Loader/Loader';
import { CircularProgress, Tooltip } from '@mui/material';
import { FETCH_POLICY_NO_CACHE } from '../../utils/constants';

interface SubjectObjectType {
    tsId: string;
    isPrimary: boolean;
    subjectBySjId: {
        subjectId: string;
        subjectName: string
        subjectOriginalName: string;
    }
}
interface TeacherObjectType {
    avatarUrl: string | null;
    mainSubject: {
        nodes: SubjectObjectType[];
    };
    teacherId: string;
    teacherName: string;
}
interface TeachersListType {
    allTeachers: {
        nodes: TeacherObjectType[];
        totalCount: number;
    }
}

interface SubjectsType {
    isPrimary: boolean;
    sjId: string;
    sjName: string;
    sjOriginalName: string;
    tsId: string;
}
interface TeacherByIdDetailsType {
    getTeacherById: {
        tId: string;
        tName: string;
        tPassword: string;
        tUserName: string;
        tAvatarUrl: string;
        //   tActive: boolean;
        mainSubject: {
            isPrimary: boolean;
            sjId: string;
            sjName: string;
            sjOriginalName: string;
            tsId: string;
        };
        otherSubjects: SubjectsType[]
    }
}

export interface AdminTeachersProps {
    isFullView: boolean;
    setisFullView: (value: boolean) => void;
    setisSearchClick: (value: boolean) => void;
    isSearchClick: boolean;
};

const AdminTeachers : React.FC<AdminTeachersProps> = ({isFullView,setisFullView,setisSearchClick,isSearchClick}) => {
    const { noUserProfile, userImage, searchCloseIcon } = images;
    const [teachersList, setTeachersList] = useState<TeachersListType>();
    const [hasMore, setHasMore] = useState(true);
    const [offset, setOffset] = useState(0);
    const limit = 50;
    const [displayForm, setDisplayForm] = useState<boolean>(false);
    const [teacherByIdDetails, setTeacherByIdDetails] = useState<TeacherByIdDetailsType>();
    const [clickAction, setClickAction] = useState("");
    const [searchValue, setSearchValue] = useState("");
    const baseUrl = process.env.REACT_APP_FRONTEND_URL;
    const teacherNameRefs = useRef<(HTMLLabelElement | null)[]>([]);
    const subjectRefs = useRef<(HTMLSpanElement | null)[]>([]);
    const [isOverflow, setIsOverflow] = useState<boolean[]>([]);
    const [isSubjectOverflow, setIsSubjectOverflow] = useState<boolean[]>([]);
  const { isMobileScreen } = getScreenDimensions();

    const [getAllTeachersList] = useLazyQuery(GET_TEACHERS_LIST, {
        fetchPolicy: FETCH_POLICY_NO_CACHE,
        onCompleted: (data) => {
            setTeachersList(prev => {
                if (!prev || offset === 0) {
                    return data;
                }
                // Merge new nodes with previous nodes
                return {
                    ...data,
                    allTeachers: {
                        ...data.allTeachers,
                        nodes: [
                            ...(prev.allTeachers?.nodes || []),
                            ...(data.allTeachers?.nodes || [])
                        ]
                    }
                };
            });
            if (data?.allTeachers?.nodes) {
                setHasMore(data.allTeachers.nodes.length === limit && (offset + limit) < (data.allTeachers.totalCount || 0));
            }
        }
    });

    const [getTeacherById] = useLazyQuery(GET_TEACHER_BY_ID, {
        fetchPolicy: FETCH_POLICY_NO_CACHE,
        onCompleted: (data) => {
            setDisplayForm(true);
            setisFullView(true);
            setClickAction('edit');
            setTeacherByIdDetails(data);
        }
    })

    
    useEffect(() => {
        setOffset(0);
        setHasMore(true);
        getAllTeachersList({
            variables: {
                orderBy: ["T_NAME_ASC"],
                searchInput: '%%',
                limit,
                offset: 0
            }
        });
    }, []);


    const handleSearchDebounce = useCallback(
        debounce((value) => {
            setSearchValue(value);
            setOffset(0);
            if(value.trim() === ""){
                setTeachersList(undefined);
            }
            if(value?.trim().length >= 3 || value.trim() === ""){
                getAllTeachersList({
                    variables: {
                        orderBy: ["T_NAME_ASC"],
                        searchInput: `%${value.trim()}%`,
                        limit,
                        offset: 0
                    }
                });
            }
        }, 500),
        [getAllTeachersList]
    );

    useEffect(() => {
        if (displayForm === false) {
            setOffset(0);
            setHasMore(true);
            getAllTeachersList({
                variables: {
                    orderBy: ["T_NAME_ASC"],
                    searchInput: `%${searchValue.trim()}%`,
                    limit,
                    offset: 0
                }
            });
        }
    }, [displayForm]);
    // Infinite scroll fetch more
    const fetchMoreTeachers = () => {
        const newOffset = offset + limit;
        setOffset(newOffset);
        getAllTeachersList({
            variables: {
                orderBy: ["T_NAME_ASC"],
                searchInput: `%${searchValue.trim()}%`,
                limit,
                offset: newOffset
            }
        });
    };

    const getTeacherDetails = (teacherId: string) => {
        getTeacherById({
            variables: {
                tId: teacherId
            }
        })
    };

    //To format staff total count
    const formatStaffCount = (count: number) => {
        if (count === 0) {
            return "No Staff";
        } else if (count === 1) {
            return "1 Staff";
        } else {
            return `${count} Staffs`;
        }
    };

    //To check the teachername is overflow
    const checkOverflow = (index: number) => {
        if (teacherNameRefs.current[index]) {
            const overflow = teacherNameRefs.current[index]!.scrollWidth > teacherNameRefs.current[index]!.clientWidth;
            setIsOverflow(prev => {
                const newOverflow = [...prev];
                newOverflow[index] = overflow;
                return newOverflow;
            });
        }
    };

     //To check the subject is overflow
     const checkSubjectOverflow = (index: number) => {
        if (subjectRefs.current[index]) {
            const overflow = subjectRefs.current[index]!.scrollWidth > subjectRefs.current[index]!.clientWidth;
            setIsSubjectOverflow(prev => {
                const newOverflow = [...prev];
                newOverflow[index] = overflow;
                return newOverflow;
            });
        }
    };

    //To check overflow in resize 
    useEffect(() => {
        if(teachersList?.allTeachers?.nodes){
            teachersList?.allTeachers?.nodes.forEach((_, index) => {
                checkOverflow(index);
                checkSubjectOverflow(index);
            });
        }
        const handleResize = () => {
            teachersList?.allTeachers?.nodes.forEach((_, index) => {
                checkOverflow(index);
                checkSubjectOverflow(index);
            });
        };

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, [teachersList]);

    const handleCloseSearch = () => {
        setisSearchClick(false);
        setSearchValue("");
        handleSearchDebounce("");
    };

    const searchOnChange = (event: { target: { value: string; }; }) => {
        setSearchValue(event.target.value);
        if (handleSearchDebounce){
          handleSearchDebounce(event?.target?.value);
        }
    };

    return (
        <>
            {
                !displayForm ?
                    <div className='AdminTeachers'>
                        {
                            (isMobileScreen && isSearchClick) ?
                            <div className={'adminTeacherSearch'}>
                                    <input id="searchBox" type='text' placeholder='Search' onChange={searchOnChange} value={searchValue} className='SearchInputFieldStyle' />
                                    {(getScreenDimensions()?.isMobileScreen && isSearchClick) && 
                                    <img src={searchCloseIcon} alt='closeIcon' className='closeIcon' onClick={handleCloseSearch} />}
                            </div>
                            :
                            <TabsContentHeader
                            title={"All Teachers"}
                            subtitle={formatStaffCount(teachersList?.allTeachers?.totalCount || 0)}
                            addLabel={"Add Teacher"}
                            setClickAction={setClickAction}
                            setDisplayForm={setDisplayForm}
                            displayForm={displayForm}
                            setisFullView={setisFullView}
                            isFullView={isFullView}
                            handleSearchDebounce={handleSearchDebounce}
                            subImage={userImage}
                            searchValue={searchValue}
                            setSearchValue={setSearchValue}
                        />}
                        {
                            (teachersList === undefined) ? <Loader bottom='150px' />
                                :
                                (
                                    <InfiniteScroll
                                        dataLength={teachersList?.allTeachers?.nodes?.length || 0}
                                        next={fetchMoreTeachers}
                                        hasMore={hasMore}
                                        loader={
                                            <div className="scroll-loader">
                                                <CircularProgress size={30} />
                                            </div>
                                        }
                                        height={`100%`}
                                        className = {`${teachersList?.allTeachers?.nodes.length ? 'teachersListContainer' : 'forNoData'}`}
                                    >
                                            {
                                                teachersList?.allTeachers?.nodes.length ?
                                                    teachersList?.allTeachers?.nodes?.map((cardItem, index) => {
                                                        return (
                                                            <div 
                                                                className='profileCardContainer' 
                                                                key={`${index}-${cardItem?.teacherId}`} 
                                                                onClick={() => getTeacherDetails(cardItem?.teacherId)}
                                                            >
                                                                <div className='profileImageContainer'>
                                                                    <img 
                                                                        src={cardItem?.avatarUrl ? `${baseUrl}${cardItem?.avatarUrl}` : noUserProfile} 
                                                                        alt='profileImage' 
                                                                        className='teacherProfileImageStyle' 
                                                                    />
                                                                </div>
                                                                <div className="nameDetails">
                                                                    <Tooltip 
                                                                    title={cardItem?.teacherName}
                                                                    disableHoverListener={!isOverflow[index]}
                                                                    placement="top"
                                                                    >
                                                                        <label 
                                                                            className="teacherName" 
                                                                            ref={(el) => (teacherNameRefs.current[index] = el)} 
                                                                            onMouseEnter={() => checkOverflow(index)}
                                                                        >
                                                                            {cardItem?.teacherName}
                                                                        </label>
                                                                    </Tooltip>
                                                                    <Tooltip 
                                                                    title={`${capitalizeFirstLetterOfEachWord(cardItem?.mainSubject?.nodes[0]?.subjectBySjId?.subjectOriginalName)} Teacher`}
                                                                    disableHoverListener={!isSubjectOverflow[index]}
                                                                    placement="top"
                                                                    >
                                                                        <span className="subjectName" ref={(el) => (subjectRefs.current[index] = el)} onMouseEnter={() => checkSubjectOverflow(index)}>
                                                                            {`${capitalizeFirstLetterOfEachWord(cardItem?.mainSubject?.nodes[0]?.subjectBySjId?.subjectOriginalName)} Teacher`}
                                                                        </span>
                                                                    </Tooltip>
                                                                    <button className="viewCta">
                                                                        <label className="view">View</label>
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        )
                                                    })
                                                    :
                                                    <div className='noDataFoundStyle'>No data found</div>
                                            }
                                    </InfiniteScroll>
                                )
                        }
                    </div> : <>
                        <AdminTeacherForm
                            setDisplayForm={setDisplayForm}
                            displayForm={displayForm}
                            setClickAction={setClickAction}
                            setisFullView={setisFullView}
                            isFullView={isFullView}
                            clickAction={clickAction}
                            teacherByIdDetails={teacherByIdDetails}
                        />
                    </>
            }
        </>
    )
}
export default AdminTeachers;
